import { httpCoreService } from "../services/HttpCoreService";
import type { ApiResponse } from "../types/auth.type";
import {
  CTMS_TASK_STATUS,
  normalizeCtmsTaskStatus,
  type CtmsTaskStatus,
} from "@/types/ctmsTaskStatus.type";
export type { CtmsTaskStatus };
export type CtmsTaskPriority = "High" | "Medium" | "Low";
export type CtmsTaskSourceModule =
  "Monitoring" | "Site Activation" | "Milestones" | "Regulatory" | "General";
export interface CtmsTaskListItem {
  id: string;
  title: string;
  description?: string | null;
  dueDate: string; // YYYY-MM-DD
  priority: CtmsTaskPriority;
  status: CtmsTaskStatus;
  sourceModule: CtmsTaskSourceModule;
  sourceEntityId?: string | null;
  sourceEntityType?: string | null;
  sourceEntityName?: string | null;
  studyId: string;
  studyTitle?: string | null;
  siteId?: string | null;
  siteName?: string | null;
  assignedToUserId?: string | null;
  assignedToName?: string | null;
  assignedByUserId?: string | null;
  assignedByName?: string | null;
  updatedAt: string;
}
export interface CtmsMyTasksResponse {
  items: CtmsTaskListItem[];
  total: number;
  page: number;
  limit: number;
}
export interface CtmsTaskComment {
  id: string;
  authorUserId?: string;
  comment: string;
  createdAt: string;
  author?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  } | null;
}
export interface CtmsTaskDetail {
  id: string;
  title: string;
  description?: string | null;
  dueDate: string;
  priority: CtmsTaskPriority;
  status: CtmsTaskStatus;
  sourceModule: CtmsTaskSourceModule;
  sourceEntityId?: string | null;
  sourceEntityType?: string | null;
  sourceEntityName?: string | null;
  studyId: string;
  siteId?: string | null;
  study?: {
    id: string;
    title?: string;
  } | null;
  site?: {
    id: string;
    name?: string;
  } | null;
  comments?: CtmsTaskComment[];
  createdAt?: string;
  updatedAt?: string;
}
export interface CtmsTaskFilters {
  studyId?: string;
  siteId?: string;
  sourceModule?: CtmsTaskSourceModule;
  status?: CtmsTaskStatus;
  priority?: CtmsTaskPriority;
  dueFrom?: string;
  dueTo?: string;
}

interface RawCtmsTask {
  id?: string;
  title?: string;
  taskName?: string;
  task_name?: string;
  description?: string | null;
  dueDate?: string;
  due_date?: string;
  priority?: CtmsTaskPriority;
  status?: string;
  sourceModule?: CtmsTaskSourceModule;
  source_module?: CtmsTaskSourceModule;
  sourceEntityId?: string | null;
  source_entity_id?: string | null;
  sourceId?: string | null;
  sourceEntityType?: string | null;
  source_entity_type?: string | null;
  sourceEntityName?: string | null;
  source_entity_name?: string | null;
  studyId?: string;
  study_id?: string;
  study?: { id: string; title?: string } | null;
  siteId?: string | null;
  site_id?: string | null;
  site?: { id: string; name?: string } | null;
  studyTitle?: string | null;
  siteName?: string | null;
  assignedToUserId?: string | null;
  assignedToName?: string | null;
  assignedByUserId?: string | null;
  assignedByName?: string | null;
  createdAt?: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
  comments?: CtmsTaskComment[];
  data?: RawCtmsTask;
}

export class CtmsTaskRepository {
  private shouldLogDebug(): boolean {
    try {
      return (
        (typeof window !== "undefined" &&
          window.localStorage?.getItem("debug_ctms_tasks") === "1") ||
        false
      );
    } catch {
      return false;
    }
  }
  private mapStatus(status: string): CtmsTaskStatus {
    return normalizeCtmsTaskStatus(status);
  }
  private mapCtmsTaskToDetail(raw: unknown): CtmsTaskDetail {
    const wrapped = raw as RawCtmsTask;
    const task = wrapped.data ?? wrapped;
    const status = this.mapStatus(task?.status ?? CTMS_TASK_STATUS.PENDING);
    const dueDate =
      typeof task?.dueDate === "string"
        ? task.dueDate
        : typeof task?.due_date === "string"
          ? task.due_date
          : task?.dueDate
            ? new Date(task.dueDate).toISOString().slice(0, 10)
            : task?.due_date
              ? new Date(task.due_date).toISOString().slice(0, 10)
              : "";
    const studyId = task?.studyId ?? task?.study_id ?? task?.study?.id ?? "";
    const siteId = task?.siteId ?? task?.site_id ?? task?.site?.id ?? null;
    return {
      id: task?.id ?? "",
      title: task?.title ?? task?.taskName ?? task?.task_name ?? "",
      description: task?.description ?? null,
      dueDate,
      priority: (task?.priority as CtmsTaskPriority) || "Medium",
      status,
      sourceModule: task?.sourceModule ?? task?.source_module ?? "General",
      sourceEntityId:
        task?.sourceEntityId ??
        task?.source_entity_id ??
        task?.sourceId ??
        null,
      sourceEntityType:
        task?.sourceEntityType ?? task?.source_entity_type ?? null,
      sourceEntityName:
        task?.sourceEntityName ?? task?.source_entity_name ?? null,
      studyId,
      siteId,
      study: task?.study ?? (studyId ? { id: studyId } : null),
      site: task?.site ?? (siteId ? { id: siteId } : null),
      createdAt: task?.createdAt ?? task?.created_at,
      updatedAt:
        task?.updatedAt ?? task?.updated_at ?? new Date().toISOString(),
      comments: task?.comments ?? [],
    };
  }
  async createTask(payload: {
    studyId: string;
    siteId?: string;
    sourceModule: CtmsTaskSourceModule;
    sourceEntityId?: string;
    sourceEntityType?: string;
    title: string;
    description?: string;
    dueDate: string; // YYYY-MM-DD
    priority: CtmsTaskPriority;
    assignedToUserId: string;
  }): Promise<CtmsTaskDetail> {
    const response: ApiResponse<CtmsTaskDetail> = await httpCoreService.post<
      CtmsTaskDetail,
      typeof payload
    >("/ctms-tasks", payload);
    const ok = response.statusCode >= 200 && response.statusCode < 300;
    if (!ok) {
      throw new Error(
        (response.error ?? response.message) || "Failed to create task",
      );
    }
    if (!response.data) {
      throw new Error(
        "Task creation succeeded but no data returned from server",
      );
    }
    return response.data;
  }
  async getMyTasks(
    page = 1,
    limit = 10,
    filters?: CtmsTaskFilters,
  ): Promise<CtmsMyTasksResponse> {
    const params: Record<string, string | number> = { page, limit };
    if (filters) {
      if (filters.studyId) {
        params.studyId = filters.studyId;
      }
      if (filters.siteId) {
        params.siteId = filters.siteId;
      }
      if (filters.sourceModule) {
        params.sourceModule = filters.sourceModule;
      }
      if (filters.status) {
        params.status = filters.status;
      }
      if (filters.priority) {
        params.priority = filters.priority;
      }
      if (filters.dueFrom) {
        params.dueFrom = filters.dueFrom;
      }
      if (filters.dueTo) {
        params.dueTo = filters.dueTo;
      }
    }
    if (this.shouldLogDebug()) {
      /* debug logging intentionally disabled */
    }
    const response: ApiResponse<CtmsMyTasksResponse> =
      await httpCoreService.get<CtmsMyTasksResponse>("/ctms-tasks/my", {
        params,
      });
    if (this.shouldLogDebug()) {
      /* debug logging intentionally disabled */
    }
    const ok = response.statusCode >= 200 && response.statusCode < 300;
    if (!ok) {
      throw new Error(
        (response.error ?? response.message) || "Failed to fetch My CTMS tasks",
      );
    }
    if (!response.data) {
      throw new Error("Request succeeded but no data returned from server");
    }
    const mappedItems: CtmsTaskListItem[] = (response.data.items || []).map(
      (item) => {
        const raw = item as RawCtmsTask;
        return {
          id: raw.id ?? item.id,
          title: raw.title ?? raw.taskName ?? raw.task_name ?? "",
          description: item.description ?? null,
          dueDate: item.dueDate,
          priority: item.priority || "Medium",
          status: this.mapStatus(item.status),
          sourceModule: item.sourceModule || "General",
          sourceEntityId: raw.sourceEntityId ?? raw.sourceId ?? null,
          sourceEntityType: item.sourceEntityType ?? null,
          sourceEntityName: raw.sourceEntityName ?? null,
          studyId: item.studyId,
          studyTitle: item.studyTitle ?? null,
          siteId: item.siteId ?? null,
          siteName: item.siteName ?? null,
          assignedToUserId: raw.assignedToUserId ?? null,
          assignedToName: raw.assignedToName ?? null,
          assignedByUserId: raw.assignedByUserId ?? null,
          assignedByName: raw.assignedByName ?? null,
          updatedAt: item.updatedAt,
        };
      },
    );
    return {
      items: mappedItems,
      total: response.data.total ?? mappedItems.length,
      page: response.data.page ?? page,
      limit: response.data.limit ?? limit,
    };
  }
  async getTasksBySource(
    sourceModule: CtmsTaskSourceModule,
    sourceEntityId: string,
    page = 1,
    limit = 100,
    studyId?: string,
  ): Promise<CtmsMyTasksResponse> {
    const response: ApiResponse<CtmsMyTasksResponse> =
      await httpCoreService.get<CtmsMyTasksResponse>("/ctms-tasks/source", {
        params: {
          sourceModule,
          sourceEntityId,
          page,
          limit,
          ...(studyId ? { studyId } : {}),
        },
      });
    const ok = response.statusCode >= 200 && response.statusCode < 300;
    if (!ok) {
      throw new Error(
        (response.error ?? response.message) || "Failed to fetch source tasks",
      );
    }
    if (!response.data) {
      throw new Error("Request succeeded but no data returned from server");
    }
    const mappedItems: CtmsTaskListItem[] = (response.data.items || []).map(
      (item) => {
        const raw = item as RawCtmsTask;
        return {
          id: raw.id ?? item.id,
          title: raw.title ?? raw.taskName ?? raw.task_name ?? "",
          description: item.description ?? null,
          dueDate: item.dueDate,
          priority: item.priority || "Medium",
          status: this.mapStatus(item.status),
          sourceModule: item.sourceModule || "General",
          sourceEntityId: raw.sourceEntityId ?? raw.sourceId ?? null,
          sourceEntityType: item.sourceEntityType ?? null,
          sourceEntityName: raw.sourceEntityName ?? null,
          studyId: item.studyId,
          studyTitle: item.studyTitle ?? null,
          siteId: item.siteId ?? null,
          siteName: item.siteName ?? null,
          assignedToUserId: raw.assignedToUserId ?? null,
          assignedToName: raw.assignedToName ?? null,
          assignedByUserId: raw.assignedByUserId ?? null,
          assignedByName: raw.assignedByName ?? null,
          updatedAt: item.updatedAt,
        };
      },
    );
    return {
      items: mappedItems,
      total: response.data.total ?? mappedItems.length,
      page: response.data.page ?? page,
      limit: response.data.limit ?? limit,
    };
  }
  async getTaskById(taskId: string): Promise<CtmsTaskDetail> {
    const response: ApiResponse<unknown> = await httpCoreService.get<unknown>(
      `/ctms-tasks/${taskId}`,
    );
    const ok = response.statusCode >= 200 && response.statusCode < 300;
    if (!ok) {
      throw new Error(
        (response.error ?? response.message) || "Failed to fetch task",
      );
    }
    if (!response.data) {
      throw new Error("Request succeeded but no data returned from server");
    }
    return this.mapCtmsTaskToDetail(response.data);
  }
  async updateStatus(
    taskId: string,
    status: CtmsTaskStatus,
    comment?: string,
  ): Promise<CtmsTaskDetail> {
    const response: ApiResponse<unknown> = await httpCoreService.patch<
      unknown,
      {
        status: CtmsTaskStatus;
        comment?: string;
      }
    >(`/ctms-tasks/${taskId}/status`, { status, comment });
    const ok = response.statusCode >= 200 && response.statusCode < 300;
    if (!ok) {
      throw new Error(
        (response.error ?? response.message) || "Failed to update status",
      );
    }
    if (!response.data) {
      throw new Error("Request succeeded but no data returned from server");
    }
    return this.mapCtmsTaskToDetail(response.data);
  }
  async addComment(taskId: string, comment: string): Promise<CtmsTaskComment> {
    const response: ApiResponse<CtmsTaskComment> = await httpCoreService.post<
      CtmsTaskComment,
      {
        comment: string;
      }
    >(`/ctms-tasks/${taskId}/comments`, { comment });
    const ok = response.statusCode >= 200 && response.statusCode < 300;
    if (!ok) {
      throw new Error(
        (response.error ?? response.message) || "Failed to add comment",
      );
    }
    if (!response.data) {
      throw new Error("Request succeeded but no data returned from server");
    }
    return response.data;
  }
}
